import { isEqual } from '@engineering11/utility'

const jobPostIdRegex = /^[^_]+(?:_[^_]+)*_[a-z]{2,}-[A-Z]{2,}$/

/**
 * Parses an application ID and extracts the job post ID and candidate ID.
 *
 * The application ID is expected to be a single string composed of both IDs,
 * separated by underscores. Since the order is not guaranteed, the function
 * progressively splits the string and uses a regex (`jobPostIdRegex`) to
 * determine which part represents the job post ID.
 *
 * This is a pure utility function and does not make any backend or network calls.
 *
 * @param value - The application ID to parse.
 * @returns An object containing `jobPostId` and `candidateId` if parsing succeeds,
 *          otherwise `undefined`.
 */
export function parseApplicationId(value: string) {
  const parts = value.split('_')

  // rebuild progressively and test
  for (let i = 1; i < parts.length; i++) {
    const left = parts.slice(0, i).join('_')
    const right = parts.slice(i).join('_')

    if (jobPostIdRegex.test(left)) {
      return {
        jobPostId: left,
        candidateId: right,
      }
    }

    if (jobPostIdRegex.test(right)) {
      return {
        jobPostId: right,
        candidateId: left,
      }
    }
  }

  return
}

/**
 * Determines whether two application IDs refer to the same job post and candidate.
 *
 * An application ID may appear in different formats depending on the context:
 * - `jobPostId_en-US_candidateId` (employer-side application)
 * - `candidateId_jobPostId_en-US` (candidate-side application view model)
 *
 * Since the order is not guaranteed, both IDs are parsed into their
 * `{ jobPostId, candidateId }` representation and compared for equality.
 *
 * @param id1 - First application ID.
 * @param id2 - Second application ID.
 * @returns `true` if both IDs resolve to the same job post and candidate,
 *          otherwise `false`.
 */
export function isSameApplicationId(id1: string, id2: string) {
  const extractedIds1 = parseApplicationId(id1)
  const extractedIds2 = parseApplicationId(id2)
  return isEqual(extractedIds1, extractedIds2)
}

/**
 * Derives the application id for the same candidate on a different job.
 * Parses the current application id, replaces the job post id with the new one,
 * and returns the composite id in the same order as the current (jobPostId first or candidateId first).
 *
 * @param currentApplicationId - The application id in the current context (e.g. from the route).
 * @param newJobPostId - The target job post id (e.g. from an application history token).
 * @returns The application id for the same candidate on the new job, or undefined if parsing fails.
 */
export function changeJobPostIdForApplication(currentApplicationId: string, newJobPostId: string): string | undefined {
  const parsed = parseApplicationId(currentApplicationId)
  if (!parsed?.candidateId) return undefined
  const { jobPostId, candidateId } = parsed
  const jobPostIdFirst = currentApplicationId.startsWith(jobPostId)
  return jobPostIdFirst ? `${newJobPostId}_${candidateId}` : `${candidateId}_${newJobPostId}`
}
